import { X, Package } from 'lucide-react'
import type { Quote } from '@api/types'

interface QuoteProduct {
  id: number
  name: string
  currency: string
  volume: number
  effectivePrice: number
  costPrice: number
  basePrice?: number
  proPrice?: number
  enterprisePrice?: number
  planPrice?: number
  vendor?: string
  discountType?: string
  discountValue?: number
  customCostPrice?: number | null
}

interface Props {
  quote: Quote
  onClose: () => void
}

function fmt(value: number | null | undefined, currency = 'AUD'): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value)
}

function fmtPrice(value: number | null | undefined, currency = 'AUD'): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(value)
}

function fmtPct(value: number | null | undefined): string {
  if (value == null) return '—'
  return `${(value * 100).toFixed(1)}%`
}

function parseProducts(json: string): QuoteProduct[] {
  try {
    const parsed = JSON.parse(json)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function getPlanBadgeColor(plan: string): string {
  switch (plan) {
    case 'Enterprise': return 'bg-purple-500/15 text-purple-400 border-purple-500/30'
    case 'Professional': return 'bg-blue-500/15 text-blue-400 border-blue-500/30'
    case 'Basic': return 'bg-gray-500/15 text-gray-400 border-gray-500/30'
    default: return 'bg-gray-500/15 text-gray-400 border-gray-500/30'
  }
}

// Overall-assessment ratings are stored lower-cased (great/good/average/bad/poor — see the
// quote mapper). Match case-insensitively and keep Excellent/Fair as aliases for safety.
function getRatingColor(rating: string | null | undefined): string {
  switch (rating?.toLowerCase()) {
    case 'great':
    case 'excellent': return 'text-green-400'
    case 'good': return 'text-blue-400'
    case 'average':
    case 'fair': return 'text-yellow-400'
    case 'bad':
    case 'poor': return 'text-red-400'
    default: return 'text-[var(--text-muted)]'
  }
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function MetricCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3">
      <div className="text-[10px] uppercase text-[var(--text-muted)] tracking-wider mb-1">{label}</div>
      <div className="text-sm font-medium text-[var(--text-primary)]">{value}</div>
      {sub && <div className="text-[10px] text-[var(--text-muted)]">{sub}</div>}
    </div>
  )
}

export function QuotePreviewModal({ quote, onClose }: Props) {
  const products = parseProducts(quote.products_json)
  const totalRevenue = products.reduce((sum, p) => sum + p.volume * p.effectivePrice, 0)
  const totalCost = products.reduce((sum, p) => sum + p.volume * p.costPrice, 0)
  const grossMargin = totalRevenue > 0 ? (totalRevenue - totalCost) / totalRevenue : 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">Linked Quote</h3>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              Quote #{quote.id} • {quote.prepared_by ? `Prepared by ${quote.prepared_by}` : 'No preparer set'}
              {quote.updated_at ? ` • Updated ${new Date(quote.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
              {quote.updated_by_name ? ` by ${quote.updated_by_name}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors">
            <X size={18} className="text-[var(--text-muted)]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Plan & Contract */}
          <div>
            <h4 className="text-sm font-medium text-[var(--text-muted)] mb-3">Plan & Contract</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3">
                <div className="text-[10px] uppercase text-[var(--text-muted)] tracking-wider mb-1">Plan</div>
                <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${getPlanBadgeColor(quote.customer_plan)}`}>
                  {quote.customer_plan || '—'}
                </span>
              </div>
              <MetricCard label="Contract Term" value={`${quote.contract_term_months || '—'} months`} />
              <MetricCard label="Payment Frequency" value={String(quote.payment_frequency || '—')} />
              <MetricCard label="Currency" value={quote.currency || '—'} />
              <MetricCard label="Start Date" value={quote.start_date ? new Date(quote.start_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'} />
              <MetricCard label="Auto Renew" value={quote.partner_commission ? 'Yes' : '—'} />
            </div>
          </div>

          {/* Fees & Pricing */}
          <div>
            <h4 className="text-sm font-medium text-[var(--text-muted)] mb-3">Fees & Pricing</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <MetricCard label="Platform Fees" value={`${fmt(quote.platform_fees, quote.currency)}/yr`} />
              <MetricCard label="Implementation Fee" value={fmt(quote.implementation_fee, quote.currency)} />
              <MetricCard label="Min. Commitment" value={fmt(quote.minimum_commitment, quote.currency)} />
              <MetricCard label="Hourly Rate" value={`${fmt(quote.hourly_rate, quote.currency)}/hr`} />
              <MetricCard label="Portal Seats" value={String(quote.portal_seats || '—')} />
              <MetricCard label="Support Level" value={quote.support_level || '—'} />
              <MetricCard label="Implementation Support" value={quote.implementation_support || '—'} />
              <MetricCard label="Hosted OneSDK" value={quote.hosted_onesdk ? 'Yes' : 'No'} />
              <MetricCard label="Partner Commission" value={quote.partner_commission ? `${quote.partner_commission}%` : '—'} />
            </div>
          </div>

          {/* Calculated Metrics */}
          {(quote.arr || quote.mrr || quote.tcv || quote.gross_profit != null) && (
            <div>
              <h4 className="text-sm font-medium text-[var(--text-muted)] mb-3">Calculated Metrics</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard label="MRR" value={fmt(quote.mrr, quote.currency)} />
                <MetricCard label="ARR" value={fmt(quote.arr, quote.currency)} />
                <MetricCard label="TCV" value={fmt(quote.tcv, quote.currency)} />
                <MetricCard label="Gross Profit" value={fmt(quote.gross_profit, quote.currency)} />
                <MetricCard label="Gross Margin" value={quote.gross_profit_margin != null ? `${quote.gross_profit_margin.toFixed(1)}%` : '—'} />
                <MetricCard label="LTV:CAC Ratio" value={quote.ltv_cac_ratio != null ? `${quote.ltv_cac_ratio.toFixed(1)}x` : '—'} />
                <MetricCard label="Payback Months" value={quote.payback_months != null ? `${quote.payback_months} mo` : '—'} />
                <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3">
                  <div className="text-[10px] uppercase text-[var(--text-muted)] tracking-wider mb-1">Overall Assessment</div>
                  <div className={`text-sm font-medium ${getRatingColor(quote.overall_assessment)}`}>
                    {quote.overall_assessment ? titleCase(quote.overall_assessment) : '—'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Products table */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Package size={14} className="text-[var(--text-muted)]" />
              <h4 className="text-sm font-medium text-[var(--text-primary)]">Products ({products.length})</h4>
            </div>
            {products.length > 0 ? (
              <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[var(--bg-secondary)]">
                      <th className="text-left px-3 py-2 text-[var(--text-muted)] font-medium">Product</th>
                      <th className="text-right px-3 py-2 text-[var(--text-muted)] font-medium w-20">Volume</th>
                      <th className="text-right px-3 py-2 text-[var(--text-muted)] font-medium w-20">Base</th>
                      <th className="text-right px-3 py-2 text-[var(--text-muted)] font-medium w-20">Effective</th>
                      <th className="text-right px-3 py-2 text-[var(--text-muted)] font-medium w-20">Cost</th>
                      <th className="text-right px-3 py-2 text-[var(--text-muted)] font-medium w-20">Margin</th>
                      <th className="text-right px-3 py-2 text-[var(--text-muted)] font-medium w-24">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, i) => {
                      const lineTotal = p.volume * p.effectivePrice
                      const lineCost = p.volume * p.costPrice
                      const lineMargin = lineTotal > 0 ? (lineTotal - lineCost) / lineTotal : 0
                      return (
                        <tr key={i} className="border-t border-[var(--border-color)]">
                          <td className="px-3 py-2 text-[var(--text-primary)]">
                            <div className="font-medium">{p.name}</div>
                            <div className="text-[10px] text-[var(--text-muted)] mt-0.5 flex gap-2">
                              {p.vendor && <span>Vendor: {p.vendor}</span>}
                              {p.discountType && p.discountType !== 'none' && p.discountValue ? (
                                <span className="text-green-400">
                                  Discount: {p.discountType === 'percent' ? `${p.discountValue}%` : fmtPrice(p.discountValue, p.currency)}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{p.volume.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right text-[var(--text-muted)]">{fmtPrice(p.planPrice || p.basePrice, p.currency)}</td>
                          <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{fmtPrice(p.effectivePrice, p.currency)}</td>
                          <td className="px-3 py-2 text-right text-[var(--text-muted)]">{fmtPrice(p.costPrice, p.currency)}</td>
                          <td className="px-3 py-2 text-right text-[var(--text-secondary)]">{fmtPct(lineMargin)}</td>
                          <td className="px-3 py-2 text-right text-[var(--text-primary)] font-medium">
                            {fmt(lineTotal, p.currency)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[var(--border-color)] bg-[var(--bg-secondary)]">
                      <td className="px-3 py-2 font-medium text-[var(--text-primary)]">Total</td>
                      <td className="px-3 py-2 text-right text-[var(--text-secondary)]">
                        {products.reduce((s, p) => s + p.volume, 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2 text-right text-[var(--text-muted)]">{fmt(totalCost, quote.currency)}</td>
                      <td className="px-3 py-2 text-right text-[var(--text-secondary)] font-medium">{fmtPct(grossMargin)}</td>
                      <td className="px-3 py-2 text-right font-medium text-[var(--text-primary)]">{fmt(totalRevenue, quote.currency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg">
                <Package size={24} className="mx-auto text-[var(--text-muted)] mb-2" />
                <p className="text-sm text-[var(--text-muted)]">No products added to this quote yet.</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {quote.notes && (
            <div>
              <h4 className="text-sm font-medium text-[var(--text-primary)] mb-2">Notes</h4>
              <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3 text-sm text-[var(--text-secondary)] whitespace-pre-wrap">
                {quote.notes}
              </div>
            </div>
          )}

          {/* Raw JSON (for debugging / full visibility) */}
          <details className="group">
            <summary className="text-xs text-[var(--text-muted)] cursor-pointer hover:text-[var(--text-secondary)] transition-colors">
              Show raw quote data
            </summary>
            <pre className="mt-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3 text-[10px] text-[var(--text-muted)] overflow-x-auto max-h-64">
              {JSON.stringify(quote, null, 2)}
            </pre>
          </details>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[var(--border-color)] flex items-center justify-between">
          <p className="text-xs text-[var(--text-muted)]">
            Quote #{quote.id} • Created {new Date(quote.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
          <button
            type="button"
            className="px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm font-medium hover:bg-[var(--bg-secondary)] transition-colors"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
